/**
 * 项目详情的 AppLovin 创意组名（Creative-set）复制区：仅投放产品唯一关联 App 可生成名称，旧多成员数据须先纠正。
 * BOOK / LANG 取项目书籍语言版，BY 取当前登录账号 CODE，OS / APP 取 App 平台与包体代号，TAG 取项目 TAG。
 */
import type { AppPlatform, LandingProjectDto } from '@novel-admin/shared';
import { useApps, useCurrentAdminAccountCode, useLandingProduct } from '~/data/queries';
import { buildApplovinCreativeSetName } from '~/lib/applovin-creative-set-name';
import { CopyableCode } from '~/ui/CopyableCode';

/** AppRow.platform 是展示值，这里换回契约平台值供 OS 段映射 */
const PLATFORM_BY_APP_ROW: Record<string, AppPlatform | undefined> = {
  iOS: 'IOS',
  Android: 'ANDROID',
  WEB: 'WEB',
};

export function LandingProjectCreativeSetNames({ project }: { project: LandingProjectDto }) {
  const product = useLandingProduct(project.product.id);
  const apps = useApps();
  const accountCode = useCurrentAdminAccountCode();
  const loading = product.isPending || apps.isLoading || accountCode.isPending;
  const failed = product.isError || apps.isError || accountCode.isError;
  const productApps = (product.data?.app_ids ?? []).map((appId) => ({
    appId,
    app: apps.data.find((row) => row.id === appId),
  }));
  return (
    <section aria-label="Creative-set 名称" className="vstack" style={{ gap: 6 }}>
      <div className="muted" style={{ fontSize: 12 }}>
        Creative-set 名称（AppLovin 创意组名，按投放产品唯一关联 App 复制）
      </div>
      {loading ? (
        <span className="muted">加载中…</span>
      ) : failed ? (
        <span role="alert" className="muted">
          Creative-set 名称加载失败，请刷新后重试
        </span>
      ) : productApps.length !== 1 ? (
        <span role="alert" className="chip chip-warn">
          投放产品关联 App 不唯一，请编辑投放产品并选择唯一 App 后重试
        </span>
      ) : (
        productApps.map(({ appId, app }) => {
          const platform = app ? PLATFORM_BY_APP_ROW[app.platform] : undefined;
          const result = platform
            ? buildApplovinCreativeSetName({
                bookCode: project.novel.book_code,
                accountCode: accountCode.data,
                platform,
                locale: project.novel.locale,
                appCode: app?.appCode,
                tag: project.tag,
              })
            : { ok: false as const, reason: 'App 不可用或未配置平台' };
          return (
            <div key={appId} className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span className="chip">{app ? `${app.name} · ${app.platform}` : appId}</span>
              {result.ok ? (
                <CopyableCode
                  value={result.name}
                  label="Creative-set 名称"
                  style={{ overflowWrap: 'anywhere' }}
                />
              ) : (
                <span className="chip chip-warn">{result.reason}</span>
              )}
            </div>
          );
        })
      )}
    </section>
  );
}
